/**
 * @gw/shared — kieu du lieu & giao thuc dung chung giua Gateway va Worker.
 */

// ---------------------------------------------------------------------------
// Trang thai Job
// ---------------------------------------------------------------------------
export type JobStatus =
  | 'queued' // dang cho trong hang doi
  | 'assigned' // da giao cho worker
  | 'processing' // worker dang xu ly
  | 'awaiting_callback' // cho webhook callback tu provider
  | 'completed' // hoan tat
  | 'failed' // that bai (het provider / loi user)
  | 'cancelled'; // bi huy

// Phan loai loi tu provider (do Error Rule Engine quyet dinh)
export type ErrorClass =
  | 'source' // loi nguon -> duoc phep failover (timeout, 502, 503, 504, busy...)
  | 'user' // loi nguoi dung -> KHONG failover (nsfw, invalid prompt, invalid image...)
  | 'retry' // thu lai cung provider
  | 'fatal'; // dung han, khong retry khong failover

// Che do thuc thi provider
export type ExecutionMode = 'gateway' | 'worker' | 'hybrid';

export type AuthType = 'none' | 'bearer' | 'api_key_header' | 'api_key_query' | 'basic';

// ---------------------------------------------------------------------------
// Giao thuc WebSocket: Worker -> Gateway
// ---------------------------------------------------------------------------
export type WorkerToGatewayMessage =
  | {
      type: 'register';
      name: string; // ten worker (hostname)
      platform: string; // win32 / linux / darwin
      version: string; // phien ban worker
      concurrency: number; // so job song song toi da
      capabilities?: string[]; // slug capability worker chay duoc (rong = tat ca)
      groups?: string[]; // ten worker group
      role?: string; // 'worker' (mac dinh) | 'server' (worker chinh tren VPS)
      cpuCores?: number; // so nhan CPU cua may
      totalMemoryMb?: number; // tong RAM (MB)
    }
  | {
      type: 'heartbeat';
      /** Tang dan moi heartbeat; Gateway echo lai de phat hien session bi lac. */
      sequence?: number;
      cpu: number; // % CPU
      memory: number; // % RAM
      activeJobs: number; // so job dang chay
      freeSlots: number; // so slot con trong
    }
  | {
      type: 'job_accepted';
      jobId: string;
      claimToken: string;
    }
  | {
      type: 'job_progress';
      jobId: string;
      claimToken: string;
      progress: number; // 0..100
      message?: string;
    }
  | {
      type: 'job_result';
      jobId: string;
      claimToken: string;
      success: boolean;
      output?: any; // ket qua chuan hoa (url anh/video, metadata...)
      error?: {
        class: ErrorClass;
        code?: string | number;
        message: string;
        providerRaw?: any; // phan hoi tho tu provider (de debug / error rule)
      };
      providerId?: string; // provider thuc su da xu ly
    }
  | { type: 'pong' };

// ---------------------------------------------------------------------------
// Giao thuc WebSocket: Gateway -> Worker
// ---------------------------------------------------------------------------
export type GatewayToWorkerMessage =
  | {
      type: 'registered';
      workerId: string; // id duy nhat do Gateway cap
      heartbeatIntervalMs: number;
      /** Server co ACK heartbeat tang ung dung; optional de rollout tuong thich ban cu. */
      heartbeatAck?: boolean;
    }
  | {
      type: 'job_assign';
      jobId: string;
      claimToken: string; // token chong ghi de ket qua boi worker "lac"
      capabilitySlug: string;
      input: any; // tham so dau vao (prompt, image...)
      // Danh sach provider theo thu tu uu tien (de worker tu failover khi duoc phep)
      providers: ProviderExecSpec[];
      leaseMs: number;
    }
  | { type: 'job_cancel'; jobId: string }
  | { type: 'ping' }
  | { type: 'heartbeat_ack'; sequence?: number }
  // Yeu cau worker kiem tra & cap nhat ngay (khong cho chu ky 6 gio).
  | { type: 'update_now' }
  // Admin doi so job chay song song (concurrency) — worker ap dung ngay (runtime).
  | { type: 'set_concurrency'; concurrency: number }
  | { type: 'error'; message: string };

// Thong tin provider gui xuong worker de thuc thi (KHONG lo api_key ra Website A,
// nhung worker la thanh phan tin cay nen nhan duoc cau hinh day du)
export interface ProviderExecSpec {
  id: string;
  name: string;
  // Danh tinh THANH VIEN nhom (de theo doi failover + circuit breaker theo tung model con).
  // `id` la providerId (nhieu member co the dung chung 1 provider) nen khong dung lam khoa member.
  modelId?: string;
  subSourceId?: string;
  memberKey?: string; // co the ghep model + subsource cho API con (vd FlipAI)
  overrideConcurrent?: number | null;
  overrideRateLimit?: number | null;
  useProviderRetryCount?: boolean;
  retryCountOverride?: number | null;
  /** Chi chay khi cac model phia tren LOI (backup). Bo qua khi overflow danh sach cho. */
  onlyOnError?: boolean;
  baseUrl: string;
  authType: AuthType;
  apiKey?: string;
  headers?: Record<string, string>;
  timeoutMs: number;
  retryCount: number;
  submitEndpoint: string;
  statusEndpoint?: string;
  cancelEndpoint?: string;
  // Cau hinh anh xa input/output dac thu provider (database-driven)
  config?: Record<string, any>;
  /**
   * Schema THAT cua model nay (ProviderModel.paramsSchema hoac fallback theo code).
   * Dung de lam sach input truoc khi gui: loai link sai loai media, sua so luong
   * (mang <-> don), quy gia tri ve option cua nguon — xem sanitizeInputForSchema.
   */
  paramsSchema?: ParamField[];
  // Error rules de worker phan loai loi (source/user) ngay tai cho
  errorRules?: ErrorRuleSpec[];
}

/**
 * Zonetoken image models use a chat model through Responses + image_generation.
 * The upstream model actually sent can be `gpt-5.5`, so detection must not rely
 * only on `injectFields.model` after param mapping.
 */
export function isZonetokenImageSpec(spec: Pick<ProviderExecSpec, 'name' | 'baseUrl' | 'config'> | null | undefined): boolean {
  if (!spec) return false;
  const cfg = (spec.config || {}) as Record<string, any>;
  const adapter = String(cfg.adapter || '').toLowerCase();
  const baseUrl = String(spec.baseUrl || '').toLowerCase();
  const name = String(spec.name || cfg.displayName || '').toLowerCase();
  // GPT Smart Provider (adapter 'gpt-smart'): CHI image ops qua codex-imagen
  // /responses. Chat van dung /chat/completions (OpenAI-compatible) vi
  // codex-imagen luon kem image_generation tool → khong the chat thuan.
  if (adapter === 'gpt-smart') {
    const op = String(cfg.vilaoOp || '').toLowerCase();
    return op === 'image' || op === 'image_edit';
  }

  const providerLooksZonetoken = adapter === 'zonetoken' || baseUrl.includes('zonetoken.net') || name.includes('zonetoken');
  if (!providerLooksZonetoken) return false;

  const modelCode = String(cfg.modelCode || '').toLowerCase();
  const injectedModel = String(cfg.injectFields?.model || '').toLowerCase();
  const displayName = String(cfg.displayName || spec.name || '').toLowerCase();
  return (
    modelCode === 'gpt-image-2-pro-max' ||
    injectedModel === 'gpt-image-2-pro-max' ||
    modelCode.includes('gpt-image') ||
    displayName.includes('gpt image') ||
    displayName.includes('image 2') ||
    (Boolean(cfg.responsesModel) && displayName.includes('image'))
  );
}

export function workerAdapterCapabilityForSpec(
  spec: Pick<ProviderExecSpec, 'name' | 'baseUrl' | 'config'> | null | undefined,
): string | null {
  if (!spec) return null;
  const adapter = String(spec.config?.adapter || '').toLowerCase();
  if (isZonetokenImageSpec(spec)) return 'adapter:zonetoken-image';
  // Nguon con (vilao) chat dung CHUNG co che Zonetoken chat (OpenAI-compatible /chat/completions)
  // -> dinh tuyen worker giong het Zonetoken cu.
  if (adapter === 'zonetoken' || adapter === 'vilao' || adapter === 'gpt-smart') return 'adapter:zonetoken-chat';
  if (adapter === 'zora') return 'adapter:zora';
  // Apiai (OpenAI-compatible, dong bo, adapter chi co o Gateway) -> ep chay Gateway:
  // khong worker nao quang ba 'adapter:apiai' nen route nay luon fallback local.run.
  if (adapter === 'apiai') return 'adapter:apiai';
  return null;
}

export function workerCanRunRoute(capabilities: string[] | null | undefined, capabilitySlug: string, role?: string | null): boolean {
  if ((role || '').toLowerCase() === 'server') return false;
  const caps = Array.isArray(capabilities) ? capabilities : [];
  if (!caps.length) return true;
  const requiresAdapterCapability = capabilitySlug.startsWith('adapter:');
  if (requiresAdapterCapability) return caps.includes(capabilitySlug);
  const normalCapabilities = caps.filter((cap) => !cap.startsWith('adapter:'));
  return !normalCapabilities.length || normalCapabilities.includes(capabilitySlug);
}

export interface ErrorRuleSpec {
  id: string;
  // Dieu kien khop: theo HTTP status hoac chuoi xuat hien trong body
  matchStatus?: number[]; // vd [502,503,504]
  matchBodyContains?: string; // vd "nsfw"
  classify: ErrorClass; // ket qua phan loai
  // BO QUA "So lan tao lai khi loi": loi do chinh dau vao quyet dinh, tao lai chac chan van
  // hong (vd Dola video 602 "Khong the tao video anh nguoi that") -> chot job ngay.
  noRetry?: boolean;
  priority: number; // rule nho hon chay truoc
}

// ---------------------------------------------------------------------------
// API cong khai cho Website A (Discovery)
// ---------------------------------------------------------------------------
export interface ManifestResponse {
  name: string;
  version: string;
  generatedAt: string;
  capabilities: CapabilitySummary[];
}

export interface CapabilitySummary {
  id: string;
  slug: string;
  name: string;
  category?: string;
  description?: string;
  providerCount: number;
  status: string;
}

// ---------------------------------------------------------------------------
// Param-schema dung chung (Admin render + Gateway catalog + seed backfill)
// ---------------------------------------------------------------------------
export * from './modelParams';

// ---------------------------------------------------------------------------
// Workflow model (kieu du lieu graph + validate + interpolate) — dung chung.
// ---------------------------------------------------------------------------
export * from './workflow';

// ---------------------------------------------------------------------------
// Catalog tu mo ta cho phan mem thu 3 (GET /api/v1/catalog) — "spec song".
// Hop nhat group / model le / sub-source thanh 1 khai niem "model" co `type`.
// ---------------------------------------------------------------------------
import type { ParamField } from './modelParams';

export type CatalogEntryType = 'group' | 'model' | 'subsource' | 'workflow';

export interface CatalogFlow {
  /** Job chay bat dong bo (submit -> poll). Luon true voi nen tang nay. */
  async: boolean;
  /** Buoc 1 cua luong 2 buoc (vd zapcap-captions sinh project_id). */
  twoStep?: boolean;
  /** Can goi catalog phu (themes/sounds) truoc khi chay (vd zapcap-render). */
  needsCatalog?: boolean;
  /** Co input anh/video/audio -> truyen URL cong khai trong input (KHONG upload len gateway). */
  hasMediaInput?: boolean;
  /** (P4) Cac loai dau vao model nhan: text | image | audio | video. */
  inputKinds?: Array<'text' | 'image' | 'audio' | 'video'>;
  /** (P4) Cach Gateway giao media cho upstream (bo thu 3 KHONG can biet): url | multipart | upstream-upload | passthrough. */
  mediaTransport?: 'gateway-url' | 'multipart' | 'upstream-upload' | 'passthrough';
  /** (P4) So anh tham chieu toi da (neu schema khai max cho field anh[]). */
  maxRefImages?: number;
}

export interface CatalogOpenAI {
  /** Endpoint OpenAI tuong duong, vd "/v1/images/generations". null = khong dien dat duoc. */
  as: string;
  /** Gia tri truyen vao truong `model` cua request OpenAI. */
  model: string;
}

export interface CatalogEntry {
  /** Id on dinh co tien to: "group:<id>" | "model:<id>" | "subsource:<id>:<op>". */
  id: string;
  type: CatalogEntryType;
  slug: string;
  name: string;
  category?: string;
  description?: string;
  /** Loai dau vao goi y: text | image | image+text | video | audio+image ... */
  inputType?: string;
  /** Loai dau ra: image | video | text | video+project ... */
  outputType?: string;
  /** Schema tham so (tu DB paramsSchema; [] neu chua khai bao). */
  paramsSchema: ParamField[];
  flow: CatalogFlow;
  /** (P3) DINH DANH CANONICAL duy nhat de submit (= gia tri trong submit.body). Khuyen nghi dung cai nay. */
  submitId?: string;
  /** Goi y submit: { endpoint, body } de phan mem thu 3 dung dung (SUBMIT VERBATIM — POST nguyen body nay). */
  submit: { endpoint: string; body: Record<string, any> };
  /** Co the goi qua lop OpenAI-compatible khong; null = chi qua /jobs. */
  openai: CatalogOpenAI | null;
}

export interface CatalogResponse {
  baseUrl: string;
  version: string;
  generatedAt: string;
  auth: { header: string; prefix: string; alt: string };
  jobLifecycle: {
    submit: string;
    poll: string;
    statuses: JobStatus[];
    terminal: JobStatus[];
  };
  webhook: { supported: boolean; field: string; events: string[]; signature: string };
  /**
   * Ảnh/video/audio: gửi URL công khai trực tiếp trong input — KHÔNG upload lên gateway.
   * `rules` = quy ước loại media theo field (ảnh vào field ảnh, video vào field video).
   */
  media: { note: string; rules?: string[] };
  /** Output đã chuẩn hoá: đọc các key này thay vì key riêng của từng nguồn. */
  output: { url: string; text: string; note: string };
  /** (P1/P3) Quy uoc tich hop: tham so canonical + submit verbatim + meta echo. */
  conventions?: {
    canonicalParams: string;
    submitVerbatim: string;
    meta: string;
  };
  zapcap?: { catalog: string; project: string };
  models: CatalogEntry[];
}

// ---------------------------------------------------------------------------
// Param mapping cho NHOM MODEL: dich tham so "chung" (canonical) cua nhom sang
// tham so thuc cua TUNG member (anh xa ten + gia tri + default). Luu o
// ModelGroupMember.paramMap, gan vao ProviderExecSpec.config.paramMap, ap dung
// ngay truoc khi dung request (per-member -> an toan failover).
// ---------------------------------------------------------------------------
export interface ParamMapField {
  /** Ten field that cua model (null = model KHONG ho tro field canonical nay -> bo). */
  to: string | null;
  /** Map gia tri khi tap option lech nhau, vd { "1k": "low", "2k": "medium" }. */
  valueMap?: Record<string, any>;
}

export interface ParamMap {
  /** canonicalName -> anh xa sang field cua member. */
  fields?: Record<string, ParamMapField>;
  /** Gia tri mac dinh cho field cua member (field ngoai canonical / client bo trong). */
  defaults?: Record<string, any>;
}

/**
 * Dich input canonical -> input rieng cua 1 member theo paramMap.
 * - Bat dau tu `defaults` (member can ma canonical khong co).
 * - Field canonical client gui: doi ten (`to`), map gia tri (`valueMap`); `to=null` -> bo.
 * - Field KHONG phai canonical (vd tham so nang cao): giu nguyen (pass-through).
 * Khong co paramMap -> tra input nguyen ven.
 */
export function applyParamMap(input: any, paramMap?: ParamMap | null): any {
  if (!paramMap || (!paramMap.fields && !paramMap.defaults)) return input;
  const src = (input && typeof input === 'object') ? input : {};
  const fields = paramMap.fields || {};
  const out: Record<string, any> = { ...(paramMap.defaults || {}) };
  for (const [key, val] of Object.entries(src)) {
    const m = fields[key];
    if (m) {
      if (m.to === null || m.to === undefined) continue; // model khong ho tro -> bo
      const mapped = m.valueMap && Object.prototype.hasOwnProperty.call(m.valueMap, String(val))
        ? m.valueMap[String(val)]
        : val;
      out[m.to] = mapped;
    } else {
      out[key] = val; // field ngoai canonical -> giu nguyen
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Chuan hoa OUTPUT: moi nguon tra key khac nhau (images/url/urls/output_url/
// outputs/image_urls...). Gateway quy ve 1 schema CHUNG cho phan mem thu 3:
//   - output_url  : URL ket qua (anh/video) — URL dau tien
//   - output_urls : tat ca URL ket qua (khi co nhieu)
//   - output_text : ket qua dang van ban (chat/text)
//   - project_id  : giu lai cho Zapcap (luong 2 buoc)
//   - raw         : giu nguyen output goc cua nguon (de debug / truong dac thu)
// ---------------------------------------------------------------------------
const OUTPUT_URL_KEYS = [
  'output_url', 'outputUrl', 'url', 'image_url', 'video_url', 'audio_url', 'cdn_url', 'preview_url',
  // `result_url` la khoa ket qua PHO BIEN NHAT (Plenxai, Videogen, Dreamface, FlipAI, Kapend,
  // TramSangTao, Watermark...). Thieu no o day thi normalizeJobOutput tra output_url RONG ->
  // buoc workflow phia sau nhan file rong ("Thieu file dau vao") va phan mem thu 3 cung
  // khong co link. Cac bien the duoi day deu quy ve dang thuong-khong-dau-gach o normKey().
  'result_url', 'raw_url', 'source_url', 'thumbnail_url', 'file_url', 'media_url', 'download_url', 'signed_url',
];
const OUTPUT_ARRAY_KEYS = [
  'output_urls', 'urls', 'images', 'image_urls', 'outputs', 'videos', 'results', 'data',
  'result_urls', 'extra_output_urls', 'media_urls', 'video_urls', 'audio_urls',
];
const OUTPUT_TEXT_KEYS = ['output_text', 'text', 'content', 'message', 'caption', 'answer', 'result_text'];
const OUTPUT_SKIP_KEYS = new Set(['raw', '__gwMeta', 'request', 'body', 'endpoint', 'base_url', 'baseUrl']);
/**
 * Khoa CHUA INPUT do nguon "vong lai" trong ket qua (echo). Chi dung o buoc du phong
 * (quet toan bo) — neu khong, anh/video DAU VAO se bi nham la ket qua.
 */
const OUTPUT_ECHO_KEYS = new Set([
  'input', 'inputs', 'params', 'parameters', 'payload', 'forwarded', 'prompt',
  'reference_images', 'reference_videos', 'referenceimages', 'referencevideos',
  'init_image', 'source_image', 'input_image', 'input_video', 'file', 'files',
]);
/** Dua ten khoa ve dang so sanh duoc: thuong + bo dau gach/gach ngang (output_url = outputUrl = OutputURL). */
const normKey = (k: string): string => String(k).toLowerCase().replace(/[^a-z0-9]/g, '');
const OUTPUT_URL_KEY_SET = new Set(OUTPUT_URL_KEYS.map(normKey));
const OUTPUT_ARRAY_KEY_SET = new Set(OUTPUT_ARRAY_KEYS.map(normKey));
/** Duoi tep media — dung o buoc du phong de khong nhat nham link tai lieu/trang web. */
const MEDIA_EXT_RE = /\.(mp4|webm|mov|m4v|avi|mkv|m3u8|png|jpe?g|webp|gif|bmp|svg|avif|heic|tiff?|mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/i;

function isOutputMediaUrl(v: string): boolean {
  return /^https?:\/\//i.test(v) || /^data:(image|video|audio)\//i.test(v);
}

function pushUrl(arr: string[], v: any) {
  if (typeof v === 'string' && isOutputMediaUrl(v)) arr.push(v);
  else if (v && typeof v === 'object' && typeof v.url === 'string' && isOutputMediaUrl(v.url)) arr.push(v.url);
}

export function collectOutputUrls(raw: any): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  const o = raw && typeof raw === 'object' ? raw : {};
  const pushUnique = (value: string) => {
    if (seen.has(value)) return;
    seen.add(value);
    urls.push(value);
  };
  const isMediaContext = (key: string, parentKey: string): boolean => {
    const normalized = normKey(key);
    const parent = normKey(parentKey);
    return (
      OUTPUT_URL_KEY_SET.has(normalized) ||
      OUTPUT_ARRAY_KEY_SET.has(normalized) ||
      parent === 'data' ||
      parent === 'result' ||
      parent === 'output' ||
      parent === 'images' ||
      parent === 'imageurls' ||
      parent === 'urls' ||
      parent === 'videos' ||
      parent === 'results' ||
      parent === 'outputs'
    );
  };

  const walk = (v: any, keyHint = '', parentKey = '') => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (isOutputMediaUrl(v) && isMediaContext(keyHint, parentKey)) pushUnique(v);
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) walk(item, keyHint, parentKey);
      return;
    }
    if (typeof v === 'object') {
      for (const [k, val] of Object.entries(v)) {
        if (OUTPUT_SKIP_KEYS.has(k)) continue;
        if (typeof val === 'string') {
          if (isOutputMediaUrl(val) && isMediaContext(k, keyHint)) pushUnique(val);
          continue;
        }
        walk(val, k, keyHint);
      }
    }
  };

  walk(raw);
  if (urls.length) return urls;

  // DU PHONG (luoi an toan): nguon dat ten khoa la (vd `videoOutput`, `cdn`, `link`) thi
  // vong quet theo danh sach khoa o tren tra ve RONG — job "hoan tat" nhung khong co link,
  // buoc workflow phia sau bao "thieu file". Khi do quet TOAN BO output, chi nhan URL co
  // duoi tep media va bo qua cac khoa "vong lai" input (anh/video dau vao).
  const fallback = (v: any, key = '') => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (isOutputMediaUrl(v) && (MEDIA_EXT_RE.test(v) || v.startsWith('data:'))) pushUnique(v);
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) fallback(item, key);
      return;
    }
    if (typeof v === 'object') {
      for (const [k, val] of Object.entries(v)) {
        if (OUTPUT_SKIP_KEYS.has(k) || OUTPUT_ECHO_KEYS.has(normKey(k))) continue;
        fallback(val, k);
      }
    }
  };
  fallback(o);
  return urls;
}

function collectOutputText(raw: any): string {
  if (typeof raw === 'string') return isOutputMediaUrl(raw) ? '' : raw;
  const o = raw && typeof raw === 'object' ? raw : {};
  const containers = [o, o.result, o.data, o.output].filter((x) => x && typeof x === 'object');
  for (const c of containers) {
    for (const k of OUTPUT_TEXT_KEYS) {
      const v = (c as any)[k];
      if (typeof v === 'string' && v.trim()) return v;
    }
  }
  const ch = o?.choices?.[0]?.message?.content;
  return typeof ch === 'string' ? ch : '';
}

/** Chuan hoa output cua job ve schema CHUNG (xem mo ta tren). null/undefined giu nguyen. */
export function normalizeJobOutput(raw: any): any {
  if (raw == null) return raw;
  const urls = collectOutputUrls(raw);
  const text = collectOutputText(raw);
  const out: Record<string, any> = {};
  if (urls.length) {
    out.output_url = urls[0];
    out.output_urls = urls;
  }
  if (text && !urls.length) out.output_text = text;
  if (raw && typeof raw === 'object' && raw.project_id != null) out.project_id = raw.project_id;
  out.raw = raw;
  return out;
}

/**
 * Tach meta noi bo Gateway (__gwMeta: { forwarded, warnings }) khoi output ket qua.
 * Gateway nhung meta vao Job.output (khong can cot DB rieng). Tra { output (da bo meta), meta }.
 *
 * Khoa bat dau bang "_" la NOI BO (vd `_forwardedUrl` = endpoint that cua nguon, dung de
 * hien chi tiet lan thu trong admin) -> KHONG lo ra cho phan mem thu 3.
 */
export function splitJobMeta(stored: any): { output: any; meta: any | null } {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return { output: stored ?? null, meta: null };
  const stashed = (stored as any).__gwMeta || null;
  if (!stashed) return { output: stored, meta: null };
  const rest = { ...(stored as any) };
  delete rest.__gwMeta;
  const isEmpty = Object.keys(rest).length === 0;
  let meta: any = stashed;
  if (stashed && typeof stashed === 'object' && !Array.isArray(stashed)) {
    meta = {};
    for (const [k, v] of Object.entries(stashed)) if (!k.startsWith('_')) meta[k] = v;
    if (!Object.keys(meta).length) meta = null;
  }
  return { output: isEmpty ? null : rest, meta };
}

export const WS_PATH = '/worker/ws';
